import { motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTransientDismiss } from '@/hooks/useTransientDismiss';

type ErrorToastProps = {
  readonly message: string;
  readonly onDismiss: () => void;
};

/** The one-line failure notice at the bottom of the window. */
export function ErrorToast({ message, onDismiss }: ErrorToastProps) {
  const ref = useTransientDismiss<HTMLDivElement>(true, onDismiss);
  const reduceMotion = useReducedMotion();
  const offset = reduceMotion ? 0 : 16;

  return (
    <motion.div
      initial={{ opacity: 0, y: offset }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: offset }}
      className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4"
    >
      <div
        ref={ref}
        role="status"
        className="border-accent/40 bg-raised/95 pointer-events-auto flex max-w-xl items-start gap-3 rounded-2xl border px-4 py-3 shadow-2xl backdrop-blur"
      >
        <span className="bg-accent mt-1.5 size-2 shrink-0 rounded-full" />
        <p className="text-sm leading-relaxed">{message}</p>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="text-faint hover:text-text -mr-1 shrink-0 transition"
        >
          <X className="size-4" />
        </button>
      </div>
    </motion.div>
  );
}
